import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  Alert,
  Chip,
  InputAdornment,
  Card,
  CardContent,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Save,
  Cancel,
  CreditCard,
} from '@mui/icons-material';
import cardPricingManager from '../utils/cardPricingManager';
import { BIN_PRODUCT_MAPPING } from '../config/cardBinConfig';

const CardPricingPanel = () => {
  const [tabValue, setTabValue] = useState(0);
  const [defaultPricing, setDefaultPricing] = useState(null);
  const [customerOverrides, setCustomerOverrides] = useState({});
  const [editingDefault, setEditingDefault] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Edit form states
  const [editedBinPrices, setEditedBinPrices] = useState({});
  const [editedDefaultPrice, setEditedDefaultPrice] = useState(10);
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerNote, setCustomerNote] = useState('');

  // Dark theme styles
  const darkInputStyles = {
    '& .MuiOutlinedInput-root': {
      color: 'white',
      '& fieldset': { borderColor: '#555' },
      '&:hover fieldset': { borderColor: '#777' },
      '&.Mui-focused fieldset': { borderColor: '#4CAF50' },
    },
    '& .MuiInputLabel-root': { color: '#999' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#4CAF50' },
  };

  useEffect(() => {
    console.log('🚀 [CardPricingPanel] Initializing...');
    cardPricingManager.initialize().then(() => {
      cardPricingManager.subscribe(() => {
        setDefaultPricing(cardPricingManager.defaultPricing);
        setCustomerOverrides(cardPricingManager.customerOverrides);
      });

      // Initial load
      setDefaultPricing(cardPricingManager.defaultPricing);
      setCustomerOverrides(cardPricingManager.customerOverrides);
    });
  }, []);

  const handleEditDefault = () => {
    setEditedBinPrices(defaultPricing?.binPrices || {});
    setEditedDefaultPrice(defaultPricing?.defaultPrice || 10);
    setEditingDefault(true);
  };

  const handleSaveDefault = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    const result = await cardPricingManager.updateDefaultPricing(
      editedBinPrices,
      editedDefaultPrice,
      'admin'
    );

    if (result.success) {
      setSuccess('Default pricing updated successfully!');
      setEditingDefault(false);
    } else {
      setError(result.error || 'Failed to update pricing');
    }
    setLoading(false);
  };

  const handleAddCustomer = () => {
    setCustomerEmail('');
    setEditedBinPrices({});
    setEditedDefaultPrice(10);
    setCustomerNote('');
    setSelectedCustomer(null);
    setEditDialog(true);
  };

  const handleEditCustomer = (email) => {
    const override = customerOverrides[email];
    setCustomerEmail(email);
    setEditedBinPrices(override.binPrices || {});
    setEditedDefaultPrice(override.defaultPrice || 10);
    setCustomerNote(override.note || '');
    setSelectedCustomer(email);
    setEditDialog(true);
  };

  const handleSaveCustomer = async () => {
    if (!customerEmail) {
      setError('Please enter customer email');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    const result = await cardPricingManager.setCustomerPricing(
      customerEmail,
      {
        binPrices: editedBinPrices,
        defaultPrice: editedDefaultPrice,
        note: customerNote,
      },
      'admin'
    );

    if (result.success) {
      setSuccess(`Pricing updated for ${customerEmail}`);
      setEditDialog(false);
    } else {
      setError(result.error || 'Failed to save pricing');
    }
    setLoading(false);
  };

  const handleDeleteCustomer = async (email) => {
    if (!window.confirm(`Remove custom pricing for ${email}?`)) return;

    setLoading(true);
    const result = await cardPricingManager.removeCustomerPricing(email);

    if (result.success) {
      setSuccess(`Pricing removed for ${email}`);
    } else {
      setError(result.error || 'Failed to remove pricing');
    }
    setLoading(false);
  };

  const updateBinPrice = (bin, price) => {
    setEditedBinPrices(prev => ({
      ...prev,
      [bin]: parseFloat(price) || 0
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, color: 'white' }}>
        <CreditCard sx={{ mr: 1, verticalAlign: 'middle' }} />
        Card Pricing Management
      </Typography>

      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs
        value={tabValue}
        onChange={(e, v) => setTabValue(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Default Pricing" />
        <Tab label={`Customer Overrides (${Object.keys(customerOverrides).length})`} />
      </Tabs>

      {/* Default Pricing Tab */}
      {tabValue === 0 && (
        <Paper sx={{ p: 3, bgcolor: '#1e1e1e' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6" sx={{ color: 'white' }}>
              Default Card Pricing
            </Typography>
            {!editingDefault ? (
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={handleEditDefault}
              >
                Edit
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<Save />}
                  onClick={handleSaveDefault}
                  disabled={loading}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={() => setEditingDefault(false)}
                >
                  Cancel
                </Button>
              </Box>
            )}
          </Box>

          <Grid container spacing={2}>
            {/* Default Price */}
            <Grid item xs={12} md={6}>
              <Card sx={{ bgcolor: '#2a2a2a' }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ color: '#999', mb: 1 }}>
                    Default Price (Fallback)
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={editingDefault ? editedDefaultPrice : (defaultPricing?.defaultPrice || 10)}
                    onChange={(e) => setEditedDefaultPrice(parseFloat(e.target.value))}
                    disabled={!editingDefault}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    sx={darkInputStyles}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* BIN-specific prices */}
            {Object.keys(BIN_PRODUCT_MAPPING).map((bin) => {
              const config = BIN_PRODUCT_MAPPING[bin];
              const currentPrice = editingDefault
                ? (editedBinPrices[bin] !== undefined ? editedBinPrices[bin] : config.price)
                : (defaultPricing?.binPrices?.[bin] || config.price);

              return (
                <Grid item xs={12} md={6} key={bin}>
                  <Card sx={{ bgcolor: '#2a2a2a' }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ color: '#999', mb: 1 }}>
                        {config.name} (BIN: {bin})
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        value={currentPrice}
                        onChange={(e) => updateBinPrice(bin, e.target.value)}
                        disabled={!editingDefault}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        sx={darkInputStyles}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      {/* Customer Overrides Tab */}
      {tabValue === 1 && (
        <Paper sx={{ p: 3, bgcolor: '#1e1e1e' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6" sx={{ color: 'white' }}>
              Customer-Specific Pricing
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddCustomer}
            >
              Add Customer
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'white' }}>Customer Email</TableCell>
                  <TableCell sx={{ color: 'white' }}>Default Price</TableCell>
                  <TableCell sx={{ color: 'white' }}>Custom BIN Prices</TableCell>
                  <TableCell sx={{ color: 'white' }}>Note</TableCell>
                  <TableCell sx={{ color: 'white' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(customerOverrides).map(([email, override]) => (
                  <TableRow key={email}>
                    <TableCell sx={{ color: 'white' }}>{email}</TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      ${override.defaultPrice !== undefined ? override.defaultPrice : '-'}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {override.binPrices && Object.keys(override.binPrices).length > 0 ? (
                        <Chip
                          label={`${Object.keys(override.binPrices).length} custom`}
                          size="small"
                          color="primary"
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>{override.note || '-'}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleEditCustomer(email)}
                        sx={{ color: '#4CAF50' }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteCustomer(email)}
                        sx={{ color: '#f44336' }}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Edit Customer Dialog */}
      <Dialog
        open={editDialog}
        onClose={() => setEditDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#1e1e1e' } }}
      >
        <DialogTitle sx={{ color: 'white' }}>
          {selectedCustomer ? `Edit Pricing for ${selectedCustomer}` : 'Add Customer Pricing'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Customer Email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              disabled={!!selectedCustomer}
              sx={{ ...darkInputStyles, mb: 2 }}
            />

            <TextField
              fullWidth
              label="Default Price"
              type="number"
              value={editedDefaultPrice}
              onChange={(e) => setEditedDefaultPrice(parseFloat(e.target.value))}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              sx={{ ...darkInputStyles, mb: 2 }}
            />

            <Typography variant="subtitle1" sx={{ color: 'white', mb: 2 }}>
              BIN-Specific Prices (Optional)
            </Typography>

            <Grid container spacing={2}>
              {Object.keys(BIN_PRODUCT_MAPPING).map((bin) => {
                const config = BIN_PRODUCT_MAPPING[bin];
                return (
                  <Grid item xs={12} md={6} key={bin}>
                    <TextField
                      fullWidth
                      label={`${config.name} (${bin})`}
                      type="number"
                      value={editedBinPrices[bin] || ''}
                      onChange={(e) => updateBinPrice(bin, e.target.value)}
                      placeholder="Use default"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      sx={darkInputStyles}
                    />
                  </Grid>
                );
              })}
            </Grid>

            <TextField
              fullWidth
              label="Note"
              value={customerNote}
              onChange={(e) => setCustomerNote(e.target.value)}
              multiline
              rows={2}
              sx={{ ...darkInputStyles, mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveCustomer}
            disabled={loading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CardPricingPanel;
